import React, { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import z from "zod";

import { Button, Divider, Form, Input, useFormContext } from "@skydio/rivet-ui";
import { TextArea } from "@skydio/rivet-ui/src/incubator/text-area/TextArea";

import { CLOUD_API_URL } from "app/env";
import { useCreateSamlIdentityProvider } from "hooks/mutation/createSamlIdentityProvider";
import { displayMutationResults } from "utils/mutations";
import { getUuidFromGlobalId } from "utils/relay";

import { SsoHelpLink } from "./SsoHelpLink";
import { ResultRow } from "./SsoResultRow";

export interface SamlUrls {
  metadataUrl: string;
  acsUrl: string;
}

export type samlInput = "metadataUrl" | "metadataFile";

interface samlInputProps {
  setInputType(type: samlInput): void;
}

export const MetadataUrlInput = ({ setInputType }: samlInputProps) => {
  const ctx = useFormContext();
  const handleSwitchMetadataInputType = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      ctx.resetField("metadataUrl");
      setInputType("metadataFile");
    },
    [ctx, setInputType]
  );
  return (
    <>
      <Input
        name="metadataUrl"
        label={
          <>
            Metadata URL <span className="text-red-500">*</span>
          </>
        }
        isClearable
        placeholder="https://provider.example.com/id/metadata.xml"
        description="URL to the SAML metadata file"
      />
      <a href="#" onClick={handleSwitchMetadataInputType}>
        Or use a file
      </a>
    </>
  );
};

export const MetadataFileInput = ({ setInputType }: samlInputProps) => {
  const ctx = useFormContext();
  const handleSwitchMetadataInputType = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      ctx.resetField("metadataUrl");
      setInputType("metadataFile");
    },
    [ctx, setInputType]
  );
  return (
    <>
      <TextArea
        name="metadataFile"
        isClearable
        label={
          <>
            Metadata File <span className="text-red-500">*</span>
          </>
        }
        description="Contents of a SAML metadata XML file"
      />
      <a href="#" onClick={handleSwitchMetadataInputType}>
        Or use a URL
      </a>
    </>
  );
};

interface NewSamlProviderPaneProps {
  organizationNodeId: string;
  connection?: string;
  onNext?: () => void;
  onCancel?: () => void;
  // newUuid?: string;
}

const newSamlSchema = z
  .object({
    name: z.string().min(1, { message: "Name must not be empty" }),
    metadataUrl: z.string().url().or(z.literal("")),
    metadataFile: z.string().optional(),
  })
  .refine(
    data => {
      return (
        (data.metadataUrl && (data.metadataFile === undefined || data.metadataFile.length < 1)) || // Only metadataUrl is set
        (data.metadataFile && data.metadataUrl === "") // Only metadataFile is set
      );
    },
    { message: "Exactly one field must be set", path: ["metadataUrl", "metadataFile"] }
  );

const NewSamlProviderPane = ({
  organizationNodeId,
  connection,
  onNext,
  onCancel,
  // newUuid,
}: NewSamlProviderPaneProps) => {
  const [inputType, setInputType] = useState<samlInput>("metadataUrl");
  const [newUuid, setNewUuid] = useState<string>(uuidv4());
  const [createSamlProvider, loading] = useCreateSamlIdentityProvider(
    displayMutationResults(response => response.createSamlIdentityProvider, {
      onSuccess: () => {
        onNext?.();
        setNewUuid(uuidv4());
      },
      successMessage: () => "SAML provider created successfully",
    })
  );

  const handleSubmit = useCallback(
    (values: z.infer<typeof newSamlSchema>) => {
      createSamlProvider(
        organizationNodeId,
        values.name,
        values.metadataUrl ? values.metadataUrl : null, // Set empty string to null
        values.metadataFile ? values.metadataFile : null, // Set empty string to null
        [connection ?? ""],
        newUuid ?? null
      );
    },
    [createSamlProvider, organizationNodeId, connection, newUuid]
  );
  const entityId = useMemo(() => {
    return `${CLOUD_API_URL}/saml/${getUuidFromGlobalId(organizationNodeId)}/${newUuid}/metadata`;
  }, [newUuid, organizationNodeId]);

  const acsUrl = useMemo(() => {
    return `${CLOUD_API_URL}/saml/${getUuidFromGlobalId(organizationNodeId)}/${newUuid}/acs`;
  }, [newUuid, organizationNodeId]);

  const defaultValues: z.infer<typeof newSamlSchema> = useMemo(() => {
    return {
      name: "",
      metadataUrl: "",
      metadataFile: "",
    };
  }, []);

  return (
    <>
      <div className="grid size-auto grid-cols-12 items-stretch justify-items-stretch gap-2">
        <ResultRow label="Entity ID" value={entityId} />
        <ResultRow label="ACS URL" value={acsUrl} />
      </div>
      <Divider />
      <Form
        schema={newSamlSchema}
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        className="flex flex-col items-start gap-2"
      >
        <div className="w-full flex-col gap-y-10">
          <Input
            name="name"
            label={
              <>
                Name <span className="text-red-500">*</span>
              </>
            }
            isClearable
            isDisabled={loading}
            placeholder="Name"
            description="Name of Identity Provider. Required"
          />
          <br />
          {inputType === "metadataUrl" ? (
            <MetadataUrlInput setInputType={setInputType} />
          ) : (
            <MetadataFileInput setInputType={setInputType} />
          )}
        </div>
        <span className="flex w-full justify-between">
          <SsoHelpLink />
          <div className="flex gap-4 self-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button isLoading={loading} id="createProvider" variant="primary" type="submit">
              Create
            </Button>
          </div>
        </span>
      </Form>
    </>
  );
};

export default NewSamlProviderPane;
